const BASE_CLASS = "scroll-reveal";
const PENDING_CLASS = "scroll-reveal--pending";
const REVEALED_CLASS = "scroll-reveal--visible";
const VARIANT_CLASS_PREFIX = "scroll-reveal--";
const DEFAULT_VARIANT = "content";
const VARIANTS = new Set(["heading", "content", "media"]);
const VIEWPORT_INSET = 96;
const REVEAL_THRESHOLD = 0.01;
const observedElements = new Set();
const focusHandlers = new WeakMap();

let observer = null;
let motionQuery = null;
let motionListenerAttached = false;

const removeFocusHandler = (element) => {
  const handler = focusHandlers.get(element);
  if (handler) {
    element.removeEventListener("focusin", handler);
    focusHandlers.delete(element);
  }
};

const getVariant = (binding) => {
  const variant = binding.arg || DEFAULT_VARIANT;
  return VARIANTS.has(variant) ? variant : DEFAULT_VARIANT;
};

const removeRevealClasses = (element) => {
  element.classList.remove(BASE_CLASS, PENDING_CLASS, REVEALED_CLASS);
  VARIANTS.forEach((variant) => {
    element.classList.remove(`${VARIANT_CLASS_PREFIX}${variant}`);
  });
};

const revealElement = (element) => {
  element.classList.add(REVEALED_CLASS);

  if (observer) {
    observer.unobserve(element);
  }
  observedElements.delete(element);
  removeFocusHandler(element);
};

const revealPendingElements = () => {
  Array.from(observedElements).forEach(revealElement);
};

const handleMotionPreferenceChange = (event) => {
  if (event.matches) {
    revealPendingElements();
  }
};

const getMotionQuery = () => {
  if (!motionQuery && typeof window.matchMedia === "function") {
    motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  }

  if (motionQuery && !motionListenerAttached) {
    if (typeof motionQuery.addEventListener === "function") {
      motionQuery.addEventListener("change", handleMotionPreferenceChange);
    } else if (typeof motionQuery.addListener === "function") {
      motionQuery.addListener(handleMotionPreferenceChange);
    }
    motionListenerAttached = true;
  }

  return motionQuery;
};

const canAnimate = () => {
  if (
    typeof window === "undefined" ||
    typeof window.IntersectionObserver !== "function"
  ) {
    return false;
  }

  const query = getMotionQuery();
  return !query || !query.matches;
};

const getObserver = () => {
  if (!observer) {
    observer = new window.IntersectionObserver(
      (entries) => {
        const latestEntries = new Map();
        entries.forEach((entry) => {
          const previousEntry = latestEntries.get(entry.target);
          if (!previousEntry || entry.time >= previousEntry.time) {
            latestEntries.set(entry.target, entry);
          }
        });

        latestEntries.forEach((entry) => {
          if (!observedElements.has(entry.target)) {
            return;
          }

          const meetsRevealThreshold =
            entry.isIntersecting &&
            entry.intersectionRatio >= REVEAL_THRESHOLD;
          const hasPassedActivationZone =
            entry.rootBounds &&
            entry.boundingClientRect.bottom <= entry.rootBounds.top;

          if (meetsRevealThreshold || hasPassedActivationZone) {
            revealElement(entry.target);
          }
        });
      },
      {
        rootMargin: `-${VIEWPORT_INSET}px 0px -${VIEWPORT_INSET}px 0px`,
        threshold: REVEAL_THRESHOLD,
      }
    );
  }

  return observer;
};

export default {
  bind(element, binding) {
    element.classList.add(
      BASE_CLASS,
      `${VARIANT_CLASS_PREFIX}${getVariant(binding)}`
    );

    if (!canAnimate()) {
      element.classList.add(REVEALED_CLASS);
      return;
    }

    element.classList.add(PENDING_CLASS);
    const focusHandler = () => revealElement(element);
    focusHandlers.set(element, focusHandler);
    element.addEventListener("focusin", focusHandler);
  },
  inserted(element) {
    if (
      !element.classList.contains(PENDING_CLASS) ||
      element.classList.contains(REVEALED_CLASS)
    ) {
      return;
    }

    if (!canAnimate()) {
      revealElement(element);
      return;
    }

    try {
      observedElements.add(element);
      getObserver().observe(element);
    } catch (error) {
      revealElement(element);
    }
  },
  unbind(element) {
    if (observer) {
      observer.unobserve(element);
    }
    observedElements.delete(element);
    removeFocusHandler(element);
    removeRevealClasses(element);
  },
};
